import React from 'react';
import inboundCallChangelog from './inbound-call.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { InboundCallState, NodeConfig, NodeField, NodeFieldType, NodeOutput, NodeType } from '../..';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Inbound Call',
  trigger: true,
  identifier: 'InboundCall',
  command_id: 'call.initiated',
  background: '#00E3AA',
  icon: 'phone',
  changelog: inboundCallChangelog,
  anchors: [
    {
      // id needs to match the event_type of the webhook
      id: 'call.initiated',
      label: 'call.initiated',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.initiated.output_data',
      type: 'source' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'call.hangup',
      id: 'call.hangup',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.hangup.output_data',
      type: 'source' as HandleType,
      variant: 'data',
      isHandle: true,
    },
  ],
  webhooks: [
    {
      webhook: 'call.initiated',
      expected: true,
      multi: false,
    },
    {
      webhook: 'call.hangup',
      expected: true,
      multi: false,
    },
  ],
};

export const state: InboundCallState = {
  connection_id: '',
};

export const fields: NodeField[] = [
  {
    id: 'connection_id',
    type: NodeFieldType.VoiceApplication,
    label: 'Voice Application',
    description:
      'Configure the voice application in your Telnyx account which enables you to receive inbound calls programatically to this workflow.',
  },
];

export const output: NodeOutput = {
  default: {
    'call.initiated': {
      call: {
        initiated: {
          call_control_id: 'string',
          connection_id: 'string',
          call_leg_id: 'string',
          custom_headers: 'object[]',
          call_session_id: 'string',
          client_state: 'string | null',
          from: 'string',
          to: 'string',
          start_time: 'string',
          direction: 'string',
          state: 'string',
        },
      },
    },
    'call.hangup': {
      call: {
        hangup: {
          call_control_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          connection_id: 'number',
          end_time: 'string',
          from: 'string',
          hangup_cause: 'string',
          hangup_source: 'string',
          sip_hangup_cause: 'string',
          start_time: 'string',
          to: 'string',
        },
      },
    },
  },
  dynamic: null,
};

export const InboundCall: React.FC<NodeType> = ({ id, selected, data }) => {
  const { background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.initiated') return null;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={`Inbound Call`} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
